/**
 * Reconnect — auto-rejoin a dropped client connection.
 *
 * PeerJS connections die routinely on mobile (screen sleep, network swap,
 * carrier handoff). When the client's host connection closes, this layer
 * walks an exponential backoff and re-runs `network.joinGame(roomCode, name)`
 * with the same identity. The Sync layer then re-issues a STATE_REQ to
 * resync the world.
 *
 * Host migration is intentionally NOT implemented here — when the host dies,
 * the room dies. Add it later if you need it.
 */
export class Reconnect {
  /**
   * @param {object} opts
   * @param {import('./Network.js').Network} opts.network
   * @param {import('./Sync.js').Sync} [opts.sync]
   * @param {number[]} [opts.backoffMs=[500, 1000, 2000, 4000, 8000]]
   * @param {(state: 'reconnecting'|'reconnected'|'gave-up', attempt: number) => void} [opts.onState]
   */
  constructor(opts) {
    this.net = opts.network;
    this.sync = opts.sync;
    this.backoff = opts.backoffMs || [500, 1000, 2000, 4000, 8000];
    this.onState = opts.onState || (() => {});

    this._enabled = false;
    this._attempt = 0;
    this._timer = null;
    this._roomCode = '';
    this._playerName = '';
  }

  /** Arm reconnection for this session. Call after a successful join. */
  enable(roomCode, playerName) {
    this._enabled = true;
    this._roomCode = roomCode;
    this._playerName = playerName;
    this._attempt = 0;

    this.net.onDisconnected = () => this._scheduleAttempt();
  }

  disable() {
    this._enabled = false;
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _scheduleAttempt() {
    if (!this._enabled) return;
    if (this._attempt >= this.backoff.length) {
      this.onState('gave-up', this._attempt);
      return;
    }
    const delay = this.backoff[this._attempt];
    this.onState('reconnecting', this._attempt + 1);
    this._timer = setTimeout(() => this._tryReconnect(), delay);
  }

  async _tryReconnect() {
    this._attempt += 1;
    try {
      // Tear down the old peer object before recreating one.
      try { this.net.destroy(); } catch (_) { /* ignore */ }
      await this.net.joinGame(this._roomCode, this._playerName);
      this._attempt = 0;
      this.onState('reconnected', 0);
      // Ask the host for the latest snapshot so the client catches up.
      if (this.sync) this.sync.clientRequestState();
    } catch (_err) {
      this._scheduleAttempt();
    }
  }
}
